import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { Button, Slider } from "antd";
import { TemperatureTrendChart } from "@/util/http/ClimateShift/responseType";
import { DownloadOutlined } from "@ant-design/icons";
import "./index.less";

// Props: data from parent (TemperatureStatResponse)
interface Props {
    data: TemperatureTrendChart;
    type: "sub2C" | "sup15C"; // 新增type参数
    activeKey?: string;
}

const ExtTempTrends = React.forwardRef((props: Props, ref) => {
    const { data, type = "sub2C", activeKey } = props;
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<echarts.EChartsType | null>(null);
    const dataPoints = data.pointValues;
    const trendLineData = data.trendLineValues;
    const extendedTrendLineData = data.extendTrendLineValues;
    const ciArea = data.confidenceIntervalValues;
    const piArea = data.predictionIntervalValues;
    // 获取浏览器可视区域宽度（更准确反映电脑屏幕尺寸）
    const getScreenWidth = () => document.documentElement.clientWidth || window.innerWidth;
    const [screenWidth, setScreenWidth] = useState(getScreenWidth());

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(getScreenWidth());
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (!chartRef.current) return;
        const chart = echarts.init(chartRef.current);
        chartInstanceRef.current = chart;

        // 屏幕尺寸变化时自适应
        function handleResize() {
            chart.resize();
        }
        window.addEventListener("resize", handleResize);

        // 判断是否为移动端
        const isMobile = screenWidth <= 768;
        const fontSize = isMobile ? 11 : 16;
        const legendFontSize = isMobile ? 10 : 13;
        const axisLabelFontSize = isMobile ? 10 : 12;
        const axisNameFontSize = isMobile ? 11 : 12;
        const titleLeft = isMobile ? "left" : "center";

        const titleText =
            type === "sub2C"
                ? "Percentage of Hours with Temperature below 2°C"
                : "Percentage of Hours with Temperature above 15°C";
        const option = {
            title: {
                text: titleText,
                left: titleLeft,
                top: 20,
                textStyle: {
                    fontSize: fontSize,
                    width: isMobile ? Math.floor(screenWidth * 0.8) : undefined,
                    overflow: isMobile ? "break" : undefined,
                    lineHeight: isMobile ? 10 : undefined,
                }
            },
            legend: {
                data: [
                    {
                        name: "Trend Line",
                        icon: "path://M 0 0 L 4 0 L 4 0.5 L 0 0.5 z",
                        itemStyle: { color: "#000000" }
                    },
                    {
                        name: "Projected Trend Line",
                        icon: "path://M 0 0 L 2 0 L 2 0.5 L 0 0.5 Z M 3 0 L 5 0 L 5 0.5 L 3 0.5 ZM 6 0 L 8 0 L 8 0.5 L 6 0.5 Z",
                        itemStyle: { color: "#000000" }
                    },
                    {
                        name: "95% Confidence Interval", icon: "rect", itemStyle: { color: "#A6A6A6" }
                    },
                    {
                        name: "95% Prediction Interval", icon: "rect", itemStyle: { color: "#D8D8D8" }
                    },
                    {
                        name: type === "sub2C" ? "Hours Below 2℃ (%)" : "Hours Above 15℃ (%)",
                        icon: "circle",
                        itemStyle: {
                            color: type === "sub2C" ? "#166CFF" : "#CF2212",
                        }
                    },
                ],
                bottom: 0,
                left: "center",
                width: 360,
                itemGap: 10,
                formatter: function (name) {
                    // 只对"Trend Line"进行空格补齐
                    const target = "Trend Line";
                    // 在原有基础上再加9个空格
                    const maxLen = "95% Confidence Interval".length + 9;
                    if (name === target) {
                        const pad = (str, len) => str.length >= len ? str : str + Array(len - str.length).fill("\u00A0").join("");
                        return pad(name, maxLen);
                    }
                    return name;
                },
                textStyle: {
                    fontSize: legendFontSize,
                    fontFamily: "Arial",
                    color: "#2F3031",
                    align: "right",
                },
            },
            grid: { show: false, left: 50, right: 20, top: 60, bottom: 120 },
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "shadow"
                },
                position: isMobile ? "inside" : undefined,
                confine: isMobile ? true : false,
                // 设置tooltip字体更小
                textStyle: {
                    fontSize: isMobile ? 11 : 14,
                },
                // 自定义tooltip位置为居中（仅移动端）
                // 也可以用position: function(point, params, dom, rect, size) { ... }自定义
                formatter: function (params) {
                    // params为数组，取第一个点
                    if (!params || !params.length) return "";
                    const year = params[0].value[0];
                    const value = params[0].value[1];
                    const name = params[0].seriesName || "";
                    const valueLabel = name === "Projected Trend Line" ? "Trend Value" : "Value";
                    return `${name}<br/>Year: ${Math.round(year)}<br/>${valueLabel}: ${Number(value).toFixed(1)}`;
                }
            },
            xAxis: {
                type: "value",
                name: "Year",
                nameLocation: "middle",
                nameGap: 30,
                interval: 5,
                min: 1990,
                max: 2105,
                axisLine: { onZero: false },
                splitLine: { show: false },
                axisLabel: {
                    fontSize: axisLabelFontSize,
                    fontFamily: "Arial",
                    color: "#2F3031",
                    formatter: function (value) {
                        // 只显示指定年份
                        const showYears = [2000, 2020, 2040, 2060, 2080, 2100];
                        return showYears.includes(Number(value)) ? value : "";
                    }
                },
                nameTextStyle: { fontSize: axisNameFontSize, fontFamily: "Arial", color: "#2F3031" },
            },
            yAxis: {
                type: "value",
                name: "Percentage of Hours",
                nameLocation: "middle",
                nameRotate: 90,
                nameGap: isMobile ? 25 : 30,
                min: (() => {
                    const step = 10;
                    const minValue = typeof data.minValue === "number" ? Math.floor(data.minValue) : 0;
                    const maxValue = typeof data.maxValue === "number" ? Math.ceil(data.maxValue) : 100;
                    let minTick = Math.floor(minValue / step) * step;
                    let maxTick = Math.ceil(maxValue / step) * step;
                    // 先保证在0-100
                    minTick = Math.max(0, minTick);
                    maxTick = Math.min(100, maxTick);
                    // 差值不足20时两端扩展
                    if (maxTick - minTick < 20) {
                        const expand = 20 - (maxTick - minTick);
                        const expandDown = Math.min(Math.floor(expand / 2 / step) * step, minTick);
                        const expandUp = expand - expandDown;
                        minTick = minTick - expandDown;
                        maxTick = maxTick + expandUp;
                        // 再次保证在0-100
                        if (minTick < 0) {
                            maxTick = Math.min(100, maxTick + Math.abs(minTick));
                            minTick = 0;
                        }
                        if (maxTick > 100) {
                            minTick = Math.max(0, minTick - (maxTick - 100));
                            maxTick = 100;
                        }
                    }
                    return minTick;
                })(),
                max: (() => {
                    const step = 10;
                    const minValue = typeof data.minValue === "number" ? Math.floor(data.minValue) : 0;
                    const maxValue = typeof data.maxValue === "number" ? Math.ceil(data.maxValue) : 100;
                    let minTick = Math.floor(minValue / step) * step;
                    let maxTick = Math.ceil(maxValue / step) * step;
                    // 先保证在0-100
                    minTick = Math.max(0, minTick);
                    maxTick = Math.min(100, maxTick);
                    // 差值不足20时两端扩展
                    if (maxTick - minTick < 20) {
                        const expand = 20 - (maxTick - minTick);
                        const expandDown = Math.min(Math.floor(expand / 2 / step) * step, minTick);
                        const expandUp = expand - expandDown;
                        minTick = minTick - expandDown;
                        maxTick = maxTick + expandUp;
                        // 再次保证在0-100
                        if (minTick < 0) {
                            maxTick = Math.min(100, maxTick + Math.abs(minTick));
                            minTick = 0;
                        }
                        if (maxTick > 100) {
                            minTick = Math.max(0, minTick - (maxTick - 100));
                            maxTick = 100;
                        }
                    }
                    return maxTick;
                })(),
                interval: 10,
                splitLine: { show: false },
                axisLabel: {
                    fontSize: axisLabelFontSize,
                    fontFamily: "Arial",
                    color: "#2F3031",
                    formatter: function (value) { return Math.round(value); }
                },
                nameTextStyle: { fontSize: axisNameFontSize, fontFamily: "Arial", color: "#2F3031" },
            },
            series: [
                {
                    name: type === "sub2C" ? "Hours Below 2℃ (%)" : "Hours Above 15℃ (%)",
                    type: "scatter",
                    symbolSize: 8,
                    data: dataPoints.map(d => [d.year, d.value]),
                    itemStyle: {
                        color: type === "sub2C" ? "#166CFF" : "#CF2212",
                    },
                    z: 10 // 让散点图层在最上层
                },
                {
                    name: "Trend Line",
                    type: "line",
                    showSymbol: false,
                    lineStyle: {
                        color: "#000000",
                        width: 2
                    },
                    data: trendLineData.map(d => [d.year, d.value]),
                    z: 9
                },
                {
                    name: "Projected Trend Line",
                    type: "line",
                    showSymbol: false,
                    lineStyle: {
                        color: "#000000",
                        width: 2, type: "dashed",
                    },
                    data: extendedTrendLineData.map(d => [d.year, d.value]),
                    z: 8
                },
                {
                    name: "95% Confidence Interval",
                    type: "line",
                    showSymbol: false,
                    areaStyle: {
                        color: "#A6A6A6"
                    },
                    lineStyle: {
                        opacity: 0,
                        color: "#A6A6A6"
                    },
                    data: ciArea.map(d => [d.year, d.value]),
                    z: 7
                },
                {
                    name: "95% Prediction Interval",
                    type: "line",
                    showSymbol: false,
                    areaStyle: {
                        color: "#D8D8D8"
                    },
                    lineStyle: {
                        opacity: 0,
                        color: "#D8D8D8"
                    },
                    data: piArea.map(d => [d.year, d.value]),
                    z: 6
                },
            ],
        };

        chart.setOption(option);
        setTimeout(() => {
            chart.resize();
        }, 0);
        return () => {
            window.removeEventListener("resize", handleResize);
            chart.dispose();
        };
    }, [data, activeKey, screenWidth]);

    // 下载图表为图片
    const handleDownloadChart = () => {
        if (chartInstanceRef.current) {
            const url = chartInstanceRef.current.getDataURL({
                type: "png",
                pixelRatio: 2,
                backgroundColor: "#fff"
            });
            const link = document.createElement("a");
            link.href = url;
            let filename = "";
            if (type === "sub2C") {
                filename = "Percentage_of_Hours_with_Temperature_below_2℃.png";
            } else if (type === "sup15C") {
                filename = "Percentage_of_Hours_with_Temperature_above_15℃.png";
            } else {
                filename = `Percentage_of_Hours_with_Temperature_${type}.png`;
            }
            link.download = filename;
            link.click();
        }
    };

    // 暴露给父组件获取图片的方法
    React.useImperativeHandle(ref, () => ({
        getChartImage: () => {
            if (chartInstanceRef.current) {
                return chartInstanceRef.current.getDataURL({
                    type: "png",
                    pixelRatio: 2,
                    backgroundColor: "#fff"
                });
            }
            return null;
        }
    }));

    return (
        <div className="extemptrend-chart">
            <div className="exttemptrend-download-btn-pos">
                <Button className="download-btn download-btn-icon-right"
                    title="Download"
                    style={{ backgroundColor: "none", padding: 0 }}
                    onClick={handleDownloadChart}>
                    <span className="download-btn-icon"><DownloadOutlined /></span>
                </Button>
            </div>
            <div ref={chartRef} className="extTempTrends-chart-responsive" />
        </div>
    );
});

ExtTempTrends.displayName = "ExtTempTrends";
export default ExtTempTrends;
